/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// 374px以下の端末では、374px幅のデザインを端末幅に応じて自動縮小する。
(() => {
  const minViewportWidth = 374;
  let viewport = document.querySelector('meta[name="viewport"]');

  if (!viewport) {
    viewport = document.createElement("meta");
    viewport.setAttribute("name", "viewport");
    document.head.appendChild(viewport);
  }

  const switchViewport = () => {
    const viewportContent = window.outerWidth > minViewportWidth
      ? "width=device-width, initial-scale=1"
      : `width=${minViewportWidth}`;

    if (viewport.getAttribute("content") !== viewportContent) {
      viewport.setAttribute("content", viewportContent);
    }
  };

  window.addEventListener("resize", switchViewport, false);
  switchViewport();
})();

(() => {
  const openMiniCartAfterMobileMenuClose = (event) => {
    const trigger = event.target.closest("[data-mobile-menu-cart-toggle]");

    if (!trigger) {
      return;
    }

    const mobileMenu = document.getElementById("mobile-menu-drawer");
    const miniCart = document.getElementById("mini-cart");

    if (!mobileMenu || !miniCart) {
      return;
    }

    event.preventDefault();

    const openMiniCart = () => {
      miniCart.open = true;
    };

    if (!mobileMenu.open) {
      openMiniCart();
      return;
    }

    let opened = false;
    const openOnce = () => {
      if (opened) {
        return;
      }

      opened = true;
      openMiniCart();
    };

    mobileMenu.addEventListener("transitionend", (transitionEvent) => {
      if (transitionEvent.target === mobileMenu && transitionEvent.propertyName === "transform") {
        openOnce();
      }
    }, { once: true });

    mobileMenu.open = false;
    window.setTimeout(openOnce, 650);
  };

  document.addEventListener("click", openMiniCartAfterMobileMenuClose);
})();

(() => {
  const updateProductItemSoldOutState = (input) => {
    const productItem = input.closest("product-item");

    if (!productItem) {
      return;
    }

    const soldOutOverlay = productItem.querySelector(".c-product__sold-out-overlay");

    if (!soldOutOverlay) {
      return;
    }

    const isVariantAvailable = input.getAttribute("data-variant-available") === "true";
    soldOutOverlay.hidden = isVariantAvailable;
  };

  const initializeProductItemSoldOutStates = (root = document) => {
    root.querySelectorAll("product-item .product-item-meta__swatch-list .color-swatch__radio:checked[data-variant-available]").forEach(updateProductItemSoldOutState);
  };

  document.addEventListener("change", (event) => {
    const input = event.target.closest?.("product-item .product-item-meta__swatch-list .color-swatch__radio[data-variant-available]");

    if (!input) {
      return;
    }

    updateProductItemSoldOutState(input);
  });

  document.addEventListener("shopify:section:load", (event) => {
    initializeProductItemSoldOutStates(event.target);
  });

  initializeProductItemSoldOutStates();
})();

(() => {
  const wishlistMetaSelector = "[data-product-card-wishlist-meta]";
  const wishlistComponentSelector = "wishlist-button-collection";

  const getSelectedVariantId = (productItem) => {
    const selectedSwatch = productItem.querySelector(".product-item-meta__swatch-list .color-swatch__radio:checked[data-variant-id]");
    const wishlistMeta = productItem.querySelector(wishlistMetaSelector);

    return selectedSwatch?.getAttribute("data-variant-id") || wishlistMeta?.getAttribute("data-variant-id");
  };

  const syncProductItemWishlistVariant = (productItem, variantId) => {
    if (!productItem) {
      return;
    }

    const selectedVariantId = variantId || getSelectedVariantId(productItem);

    if (!selectedVariantId) {
      return;
    }

    productItem.setAttribute("data-selected-variant-id", selectedVariantId);

    productItem.querySelectorAll(wishlistComponentSelector).forEach((component) => {
      if (component.getAttribute("data-variant-id") !== selectedVariantId) {
        component.setAttribute("data-variant-id", selectedVariantId);
      }
    });
  };

  const initializeProductItemWishlistVariants = (root = document) => {
    root.querySelectorAll("product-item").forEach((productItem) => {
      syncProductItemWishlistVariant(productItem);
    });
  };

  document.addEventListener("change", (event) => {
    const input = event.target.closest?.("product-item .product-item-meta__swatch-list .color-swatch__radio[data-variant-id]");

    if (!input) {
      return;
    }

    syncProductItemWishlistVariant(input.closest("product-item"), input.getAttribute("data-variant-id"));
  });

  if ("MutationObserver" in window) {
    const observer = new MutationObserver((mutationList) => {
      mutationList.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.matches("product-item")) {
            syncProductItemWishlistVariant(node);
            return;
          }

          const productItem = node.closest?.("product-item");

          if (productItem) {
            syncProductItemWishlistVariant(productItem);
          }

          node.querySelectorAll?.("product-item").forEach(syncProductItemWishlistVariant);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeProductItemWishlistVariants(event.target);
  });

  initializeProductItemWishlistVariants();
})();

(() => {
  const cardSelector = "product-item[data-product-card-wishlist-modal]";
  const wishlistTriggerSelector = "wishlist-button-collection";

  const getEventPath = (event) => {
    if (typeof event.composedPath === "function") {
      return event.composedPath();
    }

    return [];
  };

  const findElementInPath = (event, predicate) => {
    const path = getEventPath(event);

    for (const node of path) {
      if (node instanceof Element && predicate(node)) {
        return node;
      }
    }

    return null;
  };

  const findWishlistTrigger = (event) => {
    const target = event.target instanceof Element ? event.target.closest(wishlistTriggerSelector) : null;

    if (target) {
      return target;
    }

    return findElementInPath(event, (element) => element.matches(wishlistTriggerSelector));
  };

  const findWishlistCard = (event, trigger) => {
    const card = trigger?.closest(cardSelector);

    if (card) {
      return card;
    }

    return findElementInPath(event, (element) => element.matches(cardSelector));
  };

  const openModal = (modalId) => {
    if (!modalId) {
      return false;
    }

    const modal = document.getElementById(modalId);

    if (!modal) {
      return false;
    }

    if ("open" in modal) {
      modal.open = true;
    } else {
      modal.setAttribute("open", "");
      document.documentElement.classList.add("lock-all");
    }

    return true;
  };

  document.addEventListener("click", (event) => {
    const trigger = findWishlistTrigger(event);

    if (!trigger) {
      return;
    }

    const card = findWishlistCard(event, trigger);

    if (!card) {
      return;
    }

    const modalId = card.dataset.wishlistModalId;

    if (!openModal(modalId)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }, true);
})();

(() => {
  const updateSizeGuideState = (sizeGuide) => {
    const content = sizeGuide.querySelector(".c-product-size-guide__content");

    if (!content) {
      sizeGuide.classList.add("is-empty");
      return;
    }

    const hasHeight = content.getBoundingClientRect().height > 0;
    sizeGuide.classList.toggle("is-empty", !hasHeight);
  };

  const initializeSizeGuide = (sizeGuide) => {
    if (sizeGuide.dataset.sizeGuideInitialized === "true") {
      updateSizeGuideState(sizeGuide);
      return;
    }

    sizeGuide.dataset.sizeGuideInitialized = "true";
    const content = sizeGuide.querySelector(".c-product-size-guide__content");

    const scheduleUpdate = () => {
      window.requestAnimationFrame(() => updateSizeGuideState(sizeGuide));
    };

    if (content && "ResizeObserver" in window) {
      new ResizeObserver(scheduleUpdate).observe(content);
    }

    if (content && "MutationObserver" in window) {
      new MutationObserver(scheduleUpdate).observe(content, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    scheduleUpdate();
  };

  const initializeSizeGuides = (root = document) => {
    root.querySelectorAll(".c-product-size-guide").forEach(initializeSizeGuide);
  };

  document.addEventListener("shopify:section:load", (event) => {
    initializeSizeGuides(event.target);
  });

  if ("MutationObserver" in window && document.body) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches(".c-product-size-guide")) {
            initializeSizeGuide(node);
          }

          initializeSizeGuides(node);
        });
      });
    }).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  initializeSizeGuides();
})();

(() => {
  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.('form[is="product-form"]');

    if (!form || !form.querySelector('[data-variant-coming-soon-active="true"]')) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
})();

(() => {
  const triggerSelector = "[data-bis-trigger-button][data-variant-id]";
  const triggerId = "BIS_trigger";

  const syncTrigger = (button) => {
    const variantId = button?.getAttribute("data-variant-id");

    if (!variantId) {
      return;
    }

    document.querySelectorAll(`[id="${triggerId}"]`).forEach((trigger) => {
      if (trigger !== button) {
        trigger.removeAttribute("id");
      }
    });

    button.setAttribute("id", triggerId);
    button.setAttribute("data-variant-id", variantId);
  };

  const syncTriggerFromEvent = (event) => {
    const button = event.target.closest?.(triggerSelector);

    if (button) {
      syncTrigger(button);
    }
  };

  document.addEventListener("pointerdown", syncTriggerFromEvent, true);
  document.addEventListener("click", syncTriggerFromEvent, true);
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    const button = event.target.closest?.(triggerSelector);

    if (button) {
      syncTrigger(button);
    }
  }, true);
})();

(() => {
  const updateProductPoints = (form, variant) => {
    if (!form?.id || variant?.price == null) {
      return;
    }

    const productRerender = document.querySelector(`product-rerender[observe-form="${CSS.escape(form.id)}"]`);
    const pointsValue = productRerender?.querySelector("[data-product-points-value]");

    if (!pointsValue) {
      return;
    }

    const centsPerPoint = Number(pointsValue.dataset.pointsCentsPerPoint || 10000);
    const pointsRate = Number(pointsValue.dataset.pointsRate || 1);

    if (!Number.isFinite(centsPerPoint) || centsPerPoint <= 0 || !Number.isFinite(pointsRate) || pointsRate <= 0) {
      return;
    }

    const basePoints = Math.floor(Number(variant.price) / centsPerPoint);
    pointsValue.textContent = `${basePoints * pointsRate}pt`;
  };

  document.addEventListener("variant:changed", (event) => {
    updateProductPoints(event.target, event.detail.variant);
  });
})();

(() => {
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-product-description-more]");

    if (!button) {
      return;
    }

    const clip = button.closest(".product-tabs__description-clip");
    const content = clip?.querySelector(".product-tabs__tab-item-content--description");

    if (!content) {
      return;
    }

    clip.classList.add("is-expanded");
    content.classList.add("is-expanded");
  });
})();

(() => {
  const slideSelector = "slide-show-item.slideshow__slide";

  const revealSelectedSlide = (slide) => {
    if (!(slide instanceof HTMLElement) || slide.hasAttribute("hidden") || !slide.closest("slide-show")) {
      return;
    }

    slide.removeAttribute("reveal-visibility");
    slide.style.visibility = "visible";

    slide.querySelectorAll("[reveal-visibility]").forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      element.removeAttribute("reveal-visibility");
      element.style.visibility = "visible";
    });
  };

  const revealSelectedSlides = (root = document) => {
    if (!(root instanceof Document) && !(root instanceof Element)) {
      return;
    }

    root.querySelectorAll(`${slideSelector}:not([hidden])`).forEach(revealSelectedSlide);
  };

  const scheduleReveal = (root = document, delay = 0) => {
    window.setTimeout(() => {
      window.requestAnimationFrame(() => revealSelectedSlides(root));
    }, delay);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      scheduleReveal();
      scheduleReveal(document, 1200);
    }, { once: true });
  } else {
    scheduleReveal();
    scheduleReveal(document, 1200);
  }

  document.addEventListener("shopify:section:load", (event) => {
    scheduleReveal(event.target);
    scheduleReveal(event.target, 1200);
  });

  if ("MutationObserver" in window) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (!(mutation.target instanceof HTMLElement) || !mutation.target.matches(slideSelector)) {
          return;
        }

        scheduleReveal(mutation.target.closest("slide-show"), mutation.attributeName === "hidden" ? 1200 : 0);
      });
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["hidden", "reveal-visibility"],
      subtree: true
    });
  }
})();
